import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useMst } from '../../core/stores/RootStore';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { NewOrExisting, OrderStatus } from '../../models';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '60%',
    margin: 'auto',
    marginTop: theme.spacing(4),
  },
  pageLabel: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
  },
  orders: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    '& > *': {
      margin: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  button: {
    width: '100%',
  },
  listWidth: {
    width: '100%',
  },
}));

export interface DOProps extends RouteComponentProps<{}> {}

export const DigitalOrders: React.FC<DOProps> = observer(({ location }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { digitalOrdersStore, navigationStore } = useMst();
  const { isAuthenticated, user } = useAuth();
  const [existingOrders, setExistingOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      digitalOrdersStore.loadOrganizations();
      digitalOrdersStore.loadCountries();
      digitalOrdersStore.loadStates();
      loadExistingOrders();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewOrder = () => {
    const scaffoldedOrder = digitalOrdersStore.scaffold();
    navigationStore.push(`/digital-orders/${scaffoldedOrder.id}/organization?from=new`);
  };

  const getExistingOrders = async () => {
    return await digitalOrdersStore.loadDigitalOrders({
      userId: user?.profile?.sub,
      status: [OrderStatus.INCOMPLETE],
    });
  };

  const loadExistingOrders = () => {
    getExistingOrders().then(response => {
      setOrdersLoading(false);
      setExistingOrders(response);
    });
  };

  const generateNavUrl = order => {
    const keys = ['organization', 'facility', 'products', 'productDetails'];
    let existingKey;
    for (let i = 0; i < keys.length; i++) {
      if (
        keys[i] === 'facility' &&
        order.hasOwnProperty(keys[i]) &&
        order.facility.facilities.length === 0
      ) {
        existingKey = keys[i];
        break;
      }
      if (!order.hasOwnProperty(keys[i])) {
        existingKey = keys[i];
        break;
      }
    }
    switch (existingKey) {
      case 'organization':
        return `/digital-orders/${order.id}/organization`;
      case 'facility':
        return `/digital-orders/${order.id}/facilities`;
      case 'products':
        return `/digital-orders/${order.id}/products`;
      case 'productDetails':
        return `/digital-orders/${order.id}/product-details`;
      default:
        if (order.productDetails.smallBusinessUnit) {
          // summary page if we have all keys
          return `/digital-orders/${order.id}/summary`;
        }
        return `/digital-orders/${order.id}/product-details`;
    }
  };

  const handleListItemClick = order => {
    const orgId = order.organization?.organizationId;
    if (orgId) {
      digitalOrdersStore.getOrganizationFacilities(orgId).then(data => {
        navigationStore.push(generateNavUrl(order));
      });
    } else {
      navigationStore.push(generateNavUrl(order));
    }
  };

  const handleConfirmBtnClick = () => {
    digitalOrdersStore.removeDigitalOrderById(currentOrderId).then(data => {
      if (data) {
        loadExistingOrders();
      }
      closeModal();
    });
  };

  const closeModal = (): void => {
    setOpen(false);
  };

  const showModal = (orderId): void => {
    setCurrentOrderId(orderId);
    setOpen(true);
  };

  const generateListItem = elements => {
    return elements.map(order => {
      const newOrg = order.organization?.newOrExistingOrganization === NewOrExisting.NEW;
      const existingOrgName = order.organization?.organizationId
        ? digitalOrdersStore.organizationMap.get(order.organization.organizationId)?.name
        : '';
      return newOrg ? (
        <ListItem key={order.id} button onClick={() => handleListItemClick(order)}>
          <ListItemText
            primary={
              order.organization.name + ' - ' + new Date(order.updated * 1000).toLocaleString()
            }
          />
          <ListItemSecondaryAction>
            <IconButton edge="end" aria-label="delete" onClick={() => showModal(order.id)}>
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ) : (
        <ListItem key={order.id} button onClick={() => handleListItemClick(order)}>
          <ListItemText
            primary={existingOrgName + ' - ' + new Date(order.updated * 1000).toLocaleString()}
          />
          <ListItemSecondaryAction>
            <IconButton edge="end" aria-label="delete" onClick={() => showModal(order.id)}>
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      );
    });
  };

  return (
    <div className={classes.container}>
      {isAuthenticated && (
        <>
          <Typography className={classes.pageLabel}>Create New Order</Typography>
          <Paper className={classes.orders}>
            <Typography variant="h5">What would you like to do?</Typography>
            <Divider variant="middle" />
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={startNewOrder}
            >
              Start new digital order
            </Button>
            <Typography variant="h6">Continue existing digital order</Typography>
            <Grid container direction="row" justifyContent="flex-start">
              {!ordersLoading &&
                (existingOrders.length ? (
                  <List
                    className={classes.listWidth}
                    component="nav"
                    aria-label="existing digital orders"
                  >
                    {generateListItem(existingOrders)}
                  </List>
                ) : (
                  <Typography variant="h6">There are no existing digital orders</Typography>
                ))}
            </Grid>
            <ConfirmDialog
              open={open}
              title="Confirm"
              description="Are you sure you want to delete this order?"
              confirmBtn={{
                text: 'CONFIRM',
                handler: handleConfirmBtnClick,
              }}
              cancelBtn={{
                text: 'CANCEL',
                handler: closeModal,
              }}
            />
          </Paper>
        </>
      )}
    </div>
  );
});
